import type { GameStakeConfig, RoundContext } from './types';
import { playerNetOnHole, stablefordPts } from './helpers';
import { getMoneyAmount, getStakeConfig, appliesMoney } from './stakes-helpers';

export function isPerHoleStake(config: GameStakeConfig): boolean {
  return config.matchStakeMode === 'per-hole';
}

export function totalPotSettlement(
  rows: Array<{ playerKey: string; val: number }>,
  amountCents: number,
): Record<string, number> {
  if (!rows.length) return {};
  const leaderVal = rows[0].val;
  const leaders = rows.filter((r) => r.val === leaderVal);
  const losers = rows.filter((r) => r.val !== leaderVal);
  if (!losers.length) return Object.fromEntries(rows.map((r) => [r.playerKey, 0]));

  const raw: Record<string, number> = {};
  rows.forEach((r) => { raw[r.playerKey] = 0; });
  const pot = losers.length * amountCents;
  const winShare = Math.floor(pot / leaders.length);
  leaders.forEach((r) => { raw[r.playerKey] = winShare; });
  losers.forEach((r) => { raw[r.playerKey] = -amountCents; });
  return raw;
}

export function perHoleScoreSettlement(
  ctx: RoundContext,
  useNet: boolean,
  unitCents: number,
  playerKeys?: string[],
): Record<string, number> {
  const keys = playerKeys?.length ? playerKeys : ctx.players.map((p) => p.playerKey);
  const raw: Record<string, number> = {};
  keys.forEach((k) => { raw[k] = 0; });

  ctx.holes.forEach((h, i) => {
    const scored: Array<{ key: string; val: number }> = [];
    keys.forEach((key) => {
      const p = ctx.players.find((x) => x.playerKey === key);
      if (!p) return;
      const gross = ctx.scores[key]?.[i];
      if (gross == null || !Number.isFinite(gross)) return;
      let val = gross;
      if (useNet) {
        const n = playerNetOnHole(p, i, ctx.holes, ctx.scores[key] || [], 'stroke', ctx.players);
        if (n == null) return;
        val = n;
      }
      scored.push({ key, val });
    });
    if (scored.length < 2) return;

    const min = Math.min(...scored.map((s) => s.val));
    const winners = scored.filter((s) => s.val === min);
    const losers = scored.filter((s) => s.val !== min);
    if (!losers.length) return;

    const winShare = Math.floor((losers.length * unitCents) / winners.length);
    losers.forEach((l) => { raw[l.key] -= unitCents; });
    winners.forEach((w) => { raw[w.key] += winShare; });
  });

  return raw;
}

export function perHoleStablefordSettlement(
  ctx: RoundContext,
  unitCents: number,
  playerKeys?: string[],
): Record<string, number> {
  const keys = playerKeys?.length ? playerKeys : ctx.players.map((p) => p.playerKey);
  const raw: Record<string, number> = {};
  keys.forEach((k) => { raw[k] = 0; });

  ctx.holes.forEach((h, i) => {
    const scored: Array<{ key: string; val: number }> = [];
    keys.forEach((key) => {
      const p = ctx.players.find((x) => x.playerKey === key);
      if (!p) return;
      const gross = ctx.scores[key]?.[i];
      if (gross == null) return;
      const net = playerNetOnHole(p, i, ctx.holes, ctx.scores[key] || [], 'stroke', ctx.players);
      if (net == null) return;
      scored.push({ key, val: stablefordPts(net - h.par) });
    });
    if (scored.length < 2) return;

    const max = Math.max(...scored.map((s) => s.val));
    const winners = scored.filter((s) => s.val === max);
    const losers = scored.filter((s) => s.val !== max);
    if (!losers.length || max === 0) return;

    const winShare = Math.floor((losers.length * unitCents) / winners.length);
    losers.forEach((l) => { raw[l.key] -= unitCents; });
    winners.forEach((w) => { raw[w.key] += winShare; });
  });

  return raw;
}

export function perHolePuttsSettlement(ctx: RoundContext, unitCents: number): Record<string, number> {
  const keys = ctx.players.map((p) => p.playerKey);
  const raw: Record<string, number> = {};
  keys.forEach((k) => { raw[k] = 0; });

  ctx.holes.forEach((_h, i) => {
    const scored: Array<{ key: string; val: number }> = [];
    keys.forEach((key) => {
      const putts = ctx.putts?.[key]?.[i];
      if (putts == null || !Number.isFinite(putts) || putts <= 0) return;
      scored.push({ key, val: putts });
    });
    if (scored.length < 2) return;

    const min = Math.min(...scored.map((s) => s.val));
    const winners = scored.filter((s) => s.val === min);
    const losers = scored.filter((s) => s.val !== min);
    if (!losers.length) return;

    const winShare = Math.floor((losers.length * unitCents) / winners.length);
    losers.forEach((l) => { raw[l.key] -= unitCents; });
    winners.forEach((w) => { raw[w.key] += winShare; });
  });

  return raw;
}

export function strokeMoneySettlement(
  ctx: RoundContext,
  formatId: string,
  rows: Array<{ playerKey: string; name: string; val: number | null }>,
  useNet = false,
): Record<string, number> {
  const stake = getStakeConfig(ctx.stakes, formatId);
  if (!appliesMoney(stake)) return {};
  const amount = getMoneyAmount(stake) * 100;
  const valid = rows.filter((r): r is { playerKey: string; name: string; val: number } => r.val != null);
  if (!valid.length) return {};

  if (isPerHoleStake(stake)) {
    return perHoleScoreSettlement(ctx, useNet, amount, valid.map((r) => r.playerKey));
  }
  return totalPotSettlement(
    valid.map((r) => ({ playerKey: r.playerKey, val: r.val })),
    amount,
  );
}
